import React, { useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart as solidHeart } from '@fortawesome/free-solid-svg-icons';
import { faHeart as regularHeart } from '@fortawesome/free-regular-svg-icons';
import { ToastContainer, toast } from 'react-toastify';

import useToken from '../hooks/useToken';
import { SanitizedHTML } from '../utils/Helper';
import { API_ENDPOINTS } from '../utils/config';

import 'react-toastify/dist/ReactToastify.css';

/**
 * Content component - Renders a blog post with like functionality.
 * @param {Object} props 
 * @param {Object} props.data - Post data
 */
const Content = ({ data: post }) => {
    const { token } = useToken();
    const [likes, setLikes] = useState({});
    const [buttonClicked, setButtonClicked] = useState({});

    /**
     * Handles the like action and updates the backend.
     * @param {number} postId 
     * @param {number} currentCount 
     * @param {string} nonce 
     */
    const handleLike = async (postId, currentCount, nonce) => {
        const updatedCount = parseInt((likes[postId] || currentCount) + 1);
        setLikes(prev => ({ ...prev, [postId]: updatedCount }));
        setButtonClicked(prev => ({ ...prev, [postId]: true }));

        if (isNaN(updatedCount)) return;

        const payload = {
            liked_count: updatedCount,
            post_id: postId,
        };

        try {
            const response = await axios.put(
                `${API_ENDPOINTS.POSTS_ENDPOINT}/liked`,
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json; charset=UTF-8',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const message = response.data?.message || 'Action completed.';
            const notify = response.data?.status === 'success' ? toast.success : toast.error;

            notify(message, {
                position: 'bottom-left',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: 'light',
            });
        } catch (error) {
            console.error('Error liking post:', error);
            toast.error('An error occurred while liking the post.');
        }
    };

    if (!post) return null;

    const {
        id,
        title,
        featured_image,
        content,
        liked_count,
        nonce,
    } = post;

    const likeIcon = likes[id] ? solidHeart : regularHeart;
    const isLiked = buttonClicked[id] || false;

    return (
        <div className="box post-wrapper wow px-2 pb-4 fadeInUp">
            <ToastContainer />
            <div className="post-image mb-5">
                {featured_image && (
                    <img src={featured_image} alt={title} className="img-fluid" />
                )}
                <span className="liked-count-wrap text-muted">
                    <button
                        onClick={() => handleLike(id, parseInt(liked_count), nonce)}
                        disabled={isLiked}
                        aria-label="Like Post"
                    >
                        <FontAwesomeIcon icon={likeIcon} />
                    </button>
                </span>
            </div>
            <SanitizedHTML className="text-3 mt-4 text-muted post-content" html={content} tag="div" />
        </div>
    );
};

export default Content;
